import type { Report, Site } from '../types/firebase';

/** Viewer belongs to the same organization that created the report. */
export function isSameOrganizationReport(
  report: Report,
  viewerMasterOwnerId: string | undefined
): boolean {
  if (!viewerMasterOwnerId || !report?.user?.masterOwnerID) return false;
  return report.user.masterOwnerID === viewerMasterOwnerId;
}

/** PDF/download: only the owning company may export full report data. */
export function canUserDownloadReport(
  report: Report,
  viewerMasterOwnerId: string | undefined
): boolean {
  return isSameOrganizationReport(report, viewerMasterOwnerId);
}

/**
 * Public report from another org: hide scanner identity and exact location (portal behavior like mobile).
 * Private reports are only visible to owning org via Firestore rules / canUserViewReport.
 */
export function shouldRedactScannerIdentity(
  report: Report,
  viewerMasterOwnerId: string | undefined
): boolean {
  return report.privacy === 'public' && !isSameOrganizationReport(report, viewerMasterOwnerId);
}

const isSkimmerStatus = (report: Report): boolean => {
  const s = String(report.status ?? '').trim();
  return s === 'Skimmer Detected!' || s === 'Skimmer Detected';
};

/**
 * Shared feed / map: do not surface other companies' public scans that found no skimmer.
 * Private reports always stay visible to the owning org.
 */
export function includePublicReportInSharedViews(report: Report): boolean {
  if (report.privacy !== 'public') return true;
  return isSkimmerStatus(report);
}

/**
 * City/region only (no street, no custom site name) for cross-org public reports.
 */
export function getPublicApproximateLocationLabel(report: Report): string {
  const raw = report.location?.address?.trim() || '';
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
  }
  return 'Approximate area';
}

/** Site column / titles: never show another org's site name for public reports. */
export function getReportSiteNameDisplay(
  report: Report,
  viewerMasterOwnerId: string | undefined
): string {
  if (shouldRedactScannerIdentity(report, viewerMasterOwnerId)) {
    return getPublicApproximateLocationLabel(report);
  }
  const name = report.readerDetails?.siteName?.trim();
  if (name) return name;
  const addr = report.location?.address?.trim();
  return addr || '—';
}

/**
 * Folder grouping + list title (mobile parity): label is taken only from fields stored on the
 * report document — `sitesDetails.name`, then `readerDetails.siteName`, etc. — not from the
 * Sites collection. Matches mobile list rows that use `sitesDetails.name`.
 */
export function getReportSiteFolderLabel(
  report: Report,
  viewerMasterOwnerId: string | undefined
): string {
  if (shouldRedactScannerIdentity(report, viewerMasterOwnerId)) {
    return getPublicApproximateLocationLabel(report);
  }
  const sitesDetailsName = (report as Report & { sitesDetails?: { name?: string } }).sitesDetails?.name?.trim();
  if (sitesDetailsName) return sitesDetailsName;
  const readerSiteName = report.readerDetails?.siteName?.trim();
  if (readerSiteName) return readerSiteName;
  if (report.isManualEntry) return 'Manual Entry';
  const addr = report.location?.address?.trim();
  if (addr) return addr;
  return '—';
}

export function getScannerDisplayLabel(report: Report, viewerMasterOwnerId: string | undefined): string {
  if (shouldRedactScannerIdentity(report, viewerMasterOwnerId)) {
    return 'Community scan';
  }
  const name = report.user?.name?.trim();
  return name || '—';
}

/** Scanner role line for detail cards (never include email for redacted). */
export function getScannerRoleDisplay(report: Report, viewerMasterOwnerId: string | undefined): string {
  if (shouldRedactScannerIdentity(report, viewerMasterOwnerId)) {
    return 'Hidden (public report)';
  }
  const role = report.user?.role?.trim();
  const name = report.user?.name?.trim() || '—';
  return role ? `${name} (${role})` : name;
}

/**
 * Street address for list/detail: external public → site + approximate wording or city/region only.
 */
export function getReportLocationAddressDisplay(
  report: Report,
  viewerMasterOwnerId: string | undefined
): string {
  const raw = report.location?.address?.trim() || '';
  if (!shouldRedactScannerIdentity(report, viewerMasterOwnerId)) {
    return raw || '—';
  }
  return getPublicApproximateLocationLabel(report);
}

export function getResolvedSiteCoordinates(
  report: Report,
  sites: Site[]
): { lat: number; lng: number } | null {
  const siteId = report.readerDetails?.siteId;
  if (siteId) {
    const site = sites.find((s) => s.id === siteId);
    const lat = (site?.coordinates as { latitude?: number })?.latitude;
    const lng = (site?.coordinates as { longitude?: number })?.longitude;
    if (typeof lat === 'number' && typeof lng === 'number') return { lat, lng };
  }
  const sd = report.sitesDetails as { latitude?: number; longitude?: number } | undefined;
  if (sd?.latitude != null && sd?.longitude != null) {
    return { lat: sd.latitude, lng: sd.longitude };
  }
  return null;
}

function getRawScanCoordinates(report: Report): { lat: number; lng: number } | null {
  const r = report as any;
  if (r.location?.latitude != null && r.location?.longitude != null) {
    return { lat: r.location.latitude, lng: r.location.longitude };
  }
  if (r.location?.coordinates?.latitude != null && r.location?.coordinates?.longitude != null) {
    return { lat: r.location.coordinates.latitude, lng: r.location.coordinates.longitude };
  }
  if (r.readerDetails?.coordinates?.latitude != null && r.readerDetails?.coordinates?.longitude != null) {
    return { lat: r.readerDetails.coordinates.latitude, lng: r.readerDetails.coordinates.longitude };
  }
  if (r.coordinates?.latitude != null && r.coordinates?.longitude != null) {
    return { lat: r.coordinates.latitude, lng: r.coordinates.longitude };
  }
  return null;
}

/**
 * Match mobile ReportDetailsNew: if scan is very close to site center, nudge coords so the pin is not exact.
 * For other-company public reports without a site anchor, apply a small fixed offset so heatmaps are not precise GPS.
 */
export function approximateCoordinatesForPublicViewer(
  report: Report,
  viewerMasterOwnerId: string | undefined,
  sites: Site[]
): { lat: number; lng: number } | null {
  const raw = getRawScanCoordinates(report);
  if (!raw) return null;
  if (!shouldRedactScannerIdentity(report, viewerMasterOwnerId)) return raw;

  const siteCoords = getResolvedSiteCoordinates(report, sites);
  if (siteCoords) {
    const latDiff = Math.abs(siteCoords.lat - raw.lat);
    const lngDiff = Math.abs(siteCoords.lng - raw.lng);
    const lngKm = lngDiff * 111 * Math.cos((siteCoords.lat * Math.PI) / 180);
    const latKm = latDiff * 111;
    const distanceKm = Math.sqrt(latKm * latKm + lngKm * lngKm);
    if (
      distanceKm < 1 ||
      (siteCoords.lat === raw.lat && siteCoords.lng === raw.lng)
    ) {
      const latOffset = 0.009;
      const lngOffset = 0.009 / Math.cos((siteCoords.lat * Math.PI) / 180);
      return { lat: raw.lat + latOffset, lng: raw.lng + lngOffset };
    }
  }
  const latOffset = 0.008;
  const lngOffset = 0.008 / Math.cos((raw.lat * Math.PI) / 180);
  return { lat: raw.lat + latOffset, lng: raw.lng + lngOffset };
}
